// KitDialog (Estoque Fase 3b / S64) — cria/edita um kit de insumos por procedimento.
// Mostra custo (pelo custo médio) e MARGEM (preço − custo) ao vivo.
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  IconButton, MenuItem, Box, Typography, CircularProgress
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";
import { createStockKit, updateStockKit, financeErrorMessage, fmtMoney } from "../../services/financeService";

const num = (v) => Number(String(v).replace(",", ".")) || 0;

const KitDialog = ({ open, kit, products, onClose, onSaved }) => {
  const editing = !!(kit && kit.id);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [items, setItems] = useState([]); // [{ productId, qty }]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(kit?.name || "");
      setPrice(kit?.price != null ? String(kit.price) : "");
      setItems(kit?.items ? kit.items.map((i) => ({ productId: i.productId, qty: String(i.qty) })) : []);
    }
  }, [open, kit]);

  const addItem = () => setItems((a) => [...a, { productId: "", qty: "1" }]);
  const setItem = (idx, field, val) => setItems((a) => a.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  const delItem = (idx) => setItems((a) => a.filter((_, i) => i !== idx));

  const prodById = (id) => products.find((p) => Number(p.id) === Number(id));
  const cost = items.reduce((s, it) => {
    const p = prodById(it.productId);
    return s + (p ? num(it.qty) * num(p.avgCost) : 0);
  }, 0);
  const margin = price !== "" ? num(price) - cost : null;

  const handleSave = async () => {
    if (!name.trim()) { toast.warn("Informe o nome do kit."); return; }
    const cleanItems = items
      .map((it) => ({ productId: Number(it.productId), qty: num(it.qty) }))
      .filter((it) => it.productId > 0 && it.qty > 0);
    setSaving(true);
    try {
      const payload = { name, price: price !== "" ? num(price) : null, items: cleanItems };
      if (editing) await updateStockKit(kit.id, payload);
      else await createStockKit(payload);
      toast.success("Kit salvo.");
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(financeErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => (!saving ? onClose() : null)} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? "Editar kit" : "Novo kit"}</DialogTitle>
      <DialogContent>
        <TextField label="Nome do kit/procedimento *" value={name} onChange={(e) => setName(e.target.value)}
          fullWidth variant="outlined" size="small" margin="dense" autoFocus placeholder="ex.: Kit Botox testa" />
        <TextField label="Preço cobrado (R$, opcional)" type="number" value={price} onChange={(e) => setPrice(e.target.value)}
          fullWidth variant="outlined" size="small" margin="dense" inputProps={{ step: "0.01", min: "0" }} />

        <Box display="flex" alignItems="center" style={{ marginTop: 12, marginBottom: 4 }}>
          <Typography variant="subtitle2">Insumos do kit</Typography>
          <Box flexGrow={1} />
          <Button size="small" startIcon={<AddIcon />} onClick={addItem}>Adicionar</Button>
        </Box>
        {items.length === 0 && <Typography variant="caption" color="textSecondary">Nenhum insumo. Clique em "Adicionar".</Typography>}
        {items.map((it, idx) => (
          <Box key={idx} display="flex" alignItems="center" style={{ gap: 8 }}>
            <TextField select label="Produto" value={it.productId} onChange={(e) => setItem(idx, "productId", e.target.value)}
              variant="outlined" size="small" margin="dense" style={{ flex: 1 }}>
              <MenuItem value="">—</MenuItem>
              {products.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <TextField label="Qtd" type="number" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)}
              variant="outlined" size="small" margin="dense" style={{ width: 90 }} inputProps={{ step: "0.01", min: "0" }} />
            <IconButton size="small" onClick={() => delItem(idx)}><DeleteOutlineIcon fontSize="small" style={{ color: "#d32f2f" }} /></IconButton>
          </Box>
        ))}

        <Box style={{ marginTop: 12 }}>
          <Typography variant="body2">Custo do kit (médio): <b>{fmtMoney(cost)}</b></Typography>
          {margin !== null && (
            <Typography variant="body2" style={{ color: margin >= 0 ? "#2e7d32" : "#c62828" }}>
              Margem: <b>{fmtMoney(margin)}</b>{num(price) > 0 ? ` (${Math.round((margin / num(price)) * 100)}%)` : ""}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>{saving ? "Salvando..." : "Salvar"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default KitDialog;
